import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1567808291548-fc3ee04dbcf0?crop=entropy&cs=srgb&fm=jpg&q=85";

const EASE = [0.16, 1, 0.3, 1];

function MiniRing({ days, color, label, delay }) {
  const R = 26;
  const C = 2 * Math.PI * R;
  const frac = Math.min(Math.max(days, 0), 90) / 90;
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="relative w-16 h-16">
        <svg viewBox="0 0 64 64" className="w-full h-full -rotate-90">
          <circle cx="32" cy="32" r={R} fill="none" stroke="#1E222B" strokeWidth="5" />
          <motion.circle
            cx="32" cy="32" r={R} fill="none" stroke={color} strokeWidth="5" strokeLinecap="round"
            strokeDasharray={C}
            initial={{ strokeDashoffset: C }}
            animate={{ strokeDashoffset: C * (1 - frac) }}
            transition={{ duration: 1.3, delay, ease: EASE }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center font-display text-xl font-black" style={{ color }}>
          {days}
        </div>
      </div>
      <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-low">{label}</span>
    </div>
  );
}

export default function Hero({ onLaunch }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const widgetY = useTransform(scrollYProgress, [0, 1], [0, -48]);

  const lines = [
    { text: "Every car.", cls: "text-hi" },
    { text: "Road-legal.", cls: "text-outline" },
    { text: "On time.", cls: "text-ember" },
  ];

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden pt-32 pb-20">
      <div className="absolute inset-0 -z-10">
        <motion.div style={{ y: imgY }} className="absolute inset-[-8%]">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-35 spotlight" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/30 to-transparent" />
      </div>

      <div className="mx-auto max-w-7xl px-5 md:px-8 w-full grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="font-mono text-[11px] md:text-xs uppercase tracking-[0.3em] text-mid"
          >
            MOT <span className="text-ember">///</span> Council tests <span className="text-ember">///</span> Road tax
          </motion.p>

          <h1 className="font-display font-black uppercase leading-[0.88] tracking-wide text-[clamp(3.8rem,9.5vw,8.5rem)] mt-5">
            {lines.map((l, i) => (
              <span key={l.text} className="block overflow-hidden">
                <motion.span
                  className={`block ${l.cls}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease: EASE }}
                >
                  {l.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65, ease: EASE }}
            className="text-mid text-base md:text-lg max-w-xl mt-6"
          >
            Taxed &amp; Tested keeps your whole fleet's paperwork on one garage wall — live
            countdowns for every MOT, council test and road tax deadline, with email
            reminders before any of them bite.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8, ease: EASE }}
            className="flex flex-wrap items-center gap-4 mt-9"
          >
            <button
              data-testid="hero-launch-button"
              onClick={onLaunch}
              className="group rounded-full bg-ember px-8 py-4 font-display text-xl font-bold uppercase tracking-wider text-ink hover:bg-ember/90 transition-all duration-300 glow-ember"
            >
              Launch Command
            </button>
            <a
              href="#watches"
              onClick={(e) => {
                e.preventDefault();
                const el = document.querySelector("#watches");
                if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
                else el.scrollIntoView({ behavior: "smooth" });
              }}
              className="inline-flex items-center gap-2 rounded-full border border-linehi px-7 py-4 font-display text-xl font-bold uppercase tracking-wider text-hi hover:border-ember hover:text-ember transition-colors duration-300"
            >
              See what it watches
              <ArrowDown className="w-4 h-4" />
            </a>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, rotate: 1.5 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
            className="relative rounded-3xl overflow-hidden border border-linehi shadow-2xl"
          >
            <img
              src={HERO_IMG}
              alt="Fleet car in a dark garage"
              className="w-full aspect-[4/3] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
            <div className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] text-hi/80 bg-ink/60 backdrop-blur rounded-full px-3 py-1.5 border border-line">
              Bay 01 — fleet inspection
            </div>
          </motion.div>

          <motion.div
            style={{ y: widgetY }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
            className="mt-6 lg:mt-0 lg:absolute lg:-bottom-14 lg:-left-16 rounded-2xl border border-linehi bg-panel/90 backdrop-blur-xl p-6 shadow-2xl"
            data-testid="hero-countdown-preview"
          >
            <div className="flex items-center justify-between gap-8">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-low">Live compliance preview</span>
              <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-mint">
                <span className="w-1.5 h-1.5 rounded-full bg-mint animate-pulse-dot" /> On watch
              </span>
            </div>
            <div className="grid grid-cols-3 gap-6 mt-5">
              <MiniRing days={5} color="#FF3815" label="MOT" delay={1.2} />
              <MiniRing days={12} color="#FFB800" label="Council" delay={1.35} />
              <MiniRing days={64} color="#00E599" label="Road tax" delay={1.5} />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
